import type { StoreHit } from '@/lib/algolia/stores';
import type { ProductHit } from '@/lib/algolia/types';

export const AUTOCOMPLETE_SUGGESTIONS_PER_TYPE = 4;
/** Fetch extra hits so we can still show 4 unique labels after variant dedupe. */
export const AUTOCOMPLETE_FETCH_BUFFER = AUTOCOMPLETE_SUGGESTIONS_PER_TYPE * 3;

function dedupeByKey<T>(items: T[], getKey: (item: T) => string, limit: number): T[] {
  const seen = new Set<string>();
  const unique: T[] = [];

  for (const item of items) {
    const key = getKey(item);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(item);
    if (unique.length >= limit) {
      break;
    }
  }

  return unique;
}

function collectionSuggestionHit(collectionName: string, localeCode: string): ProductHit {
  return {
    objectID: `collection:${collectionName}`,
    product_type: 'Watch',
    name: { [localeCode]: collectionName, EN_US: collectionName },
    collection: collectionName,
  } as unknown as ProductHit;
}

function compactSearchText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Keep collections whose name matches the query, including a one-letter typo like "multicron". */
export function collectionNameMatchesQuery(collectionName: string, query: string): boolean {
  const queryText = compactSearchText(query);
  const collectionText = compactSearchText(collectionName);
  if (!queryText) {
    return false;
  }
  if (collectionText.includes(queryText)) {
    return true;
  }

  let prefixLength = 0;
  const limit = Math.min(queryText.length, collectionText.length);
  while (prefixLength < limit && queryText[prefixLength] === collectionText[prefixLength]) {
    prefixLength += 1;
  }
  return prefixLength >= 6 && queryText.length <= collectionText.length + 2;
}

export function getProductAutocompleteDedupeKey(hit: ProductHit, localeCode: string): string {
  if (hit.product_type === 'Watch' && hit.model?.slug) {
    return `Watch:${hit.model.slug}`;
  }

  const name = hit.name?.[localeCode] || hit.name?.EN_US || hit.objectID;
  return `${hit.product_type}:${name.trim().toLowerCase()}`;
}

/**
 * On Gallet, a collection-name query (e.g. "multicron") should propose each
 * matching collection. Other queries keep model-slug product suggestions.
 */
export function buildWatchAutocompleteSuggestions(
  hits: ProductHit[],
  collectionFacets: Record<string, number> | undefined,
  localeCode: string,
  limit = AUTOCOMPLETE_SUGGESTIONS_PER_TYPE,
  query = '',
  useCollectionSuggestions = false
): ProductHit[] {
  if (!useCollectionSuggestions) {
    return dedupeProductHitsForAutocomplete(hits, localeCode, limit);
  }

  const matchingCollections = Object.entries(collectionFacets ?? {})
    .sort(([, leftCount], [, rightCount]) => rightCount - leftCount)
    .map(([name]) => name.trim())
    .filter((name) => name && query.trim() && collectionNameMatchesQuery(name, query));

  if (matchingCollections.length === 0) {
    return dedupeProductHitsForAutocomplete(hits, localeCode, limit);
  }

  return matchingCollections
    .slice(0, limit)
    .map((collectionName) => collectionSuggestionHit(collectionName, localeCode));
}

export function dedupeProductHitsForAutocomplete(
  hits: ProductHit[],
  localeCode: string,
  limit = AUTOCOMPLETE_SUGGESTIONS_PER_TYPE
): ProductHit[] {
  return dedupeByKey(hits, (hit) => getProductAutocompleteDedupeKey(hit, localeCode), limit);
}

export function dedupeStoreHitsForAutocomplete(
  hits: StoreHit[],
  limit = AUTOCOMPLETE_SUGGESTIONS_PER_TYPE
): StoreHit[] {
  return dedupeByKey(hits, (hit) => hit.slug || hit.objectID, limit);
}
